/** A* over a DLR2Graph — plain shortest-path (edge meters as cost,
 * haversine-to-target as the admissible heuristic), the browser twin of
 * Router.route(from:to:) on the phone. Async because every vertex/edge
 * read may hit the network; the block cache keeps the corridor cheap.
 * Cooperatively cancellable (AbortSignal) and budgetable (deadlineMs) so a
 * caller can run one long search and give up on it cleanly instead of
 * leaving a zombie promise running after the UI has moved on. */

import type { DLR2Graph } from "./dlr2";
import { haversineM } from "./geo";
import type { LonLat } from "./types";

export interface RouteResult {
  /** Vertex ids from `from` to `to` inclusive. */
  path: number[];
  meters: number;
  /** The path's coordinates, resolved for rendering. */
  coords: LonLat[];
  expansions: number;
  /** wayIds[i] = the wayId of the edge traversed from path[i] to
   * path[i+1] (length = path.length - 1). Lets callers map the solved
   * route back onto way attributes — e.g. seasonal closures. */
  wayIds: number[];
}

export interface RouteOptions {
  /** Give up (return null) past this many node expansions — an honest
   * straight-leg fallback beats an unbounded stall. Default 400_000. */
  maxExpansions?: number;
  /** Abort the search early — checked on every heap pop. Same degrade
   * path as maxExpansions: a clean null, not a throw. */
  signal?: AbortSignal;
  /** Wall-clock budget in ms from call start. Checked every 64 expansions
   * rather than on every pop — a performance.now() read is cheap but not
   * free, and expansions happen far more often than the budget needs
   * re-checking. Either trip degrades the same way maxExpansions does. */
  deadlineMs?: number;
}

const DEFAULT_MAX_EXPANSIONS = 400_000;

/** Minimal binary min-heap keyed by priority — mirrors Router.swift's
 * private Heap exactly (push always appends + sifts up; pop swaps the
 * last element to the root + sifts down; no decrease-key, stale entries
 * for an already-improved vertex are just popped and re-relaxed, which is
 * harmless since relaxation only ever acts on the current best g-score). */
class MinHeap {
  private items: { id: number; p: number }[] = [];

  push(id: number, p: number): void {
    const items = this.items;
    items.push({ id, p });
    let i = items.length - 1;
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (items[parent].p <= items[i].p) break;
      [items[parent], items[i]] = [items[i], items[parent]];
      i = parent;
    }
  }

  pop(): number | null {
    const items = this.items;
    if (items.length === 0) return null;
    const top = items[0];
    const last = items.pop()!;
    if (items.length > 0) {
      items[0] = last;
      let i = 0;
      for (;;) {
        const l = 2 * i + 1;
        const r = 2 * i + 2;
        let m = i;
        if (l < items.length && items[l].p < items[m].p) m = l;
        if (r < items.length && items[r].p < items[m].p) m = r;
        if (m === i) break;
        [items[m], items[i]] = [items[i], items[m]];
        i = m;
      }
    }
    return top.id;
  }

  /** Approximate top-`n` ids — reads items[0..n) directly rather than
   * popping, so it's O(n) and doesn't disturb the heap. Only the true
   * root (items[0]) is guaranteed to be the minimum; the rest are just
   * "probably near the front." That's fine for its one caller: picking
   * likely-next-expansion candidates to prefetch is a guess either way. */
  topIds(n: number): number[] {
    const items = this.items;
    const count = Math.min(n, items.length);
    const out: number[] = new Array(count);
    for (let i = 0; i < count; i++) out[i] = items[i].id;
    return out;
  }
}

export async function route(
  g: DLR2Graph,
  from: number,
  to: number,
  opts?: RouteOptions,
): Promise<RouteResult | null> {
  const maxExpansions = opts?.maxExpansions ?? DEFAULT_MAX_EXPANSIONS;
  const signal = opts?.signal;
  const deadlineMs = opts?.deadlineMs;

  // An already-tripped signal or an already-exhausted budget degrades
  // exactly like maxExpansions: an honest null, checked before any work
  // at all (not even the trivial from===to shortcut) rather than a
  // half-started search nobody wanted.
  if (signal?.aborted) return null;
  if (deadlineMs != null && deadlineMs <= 0) return null;

  if (from === to) {
    const at = await g.vertex(from);
    return { path: [from], meters: 0, coords: [at], expansions: 0, wayIds: [] };
  }

  const targetCoord = await g.vertex(to);
  const gScore = new Map<number, number>();
  const cameFrom = new Map<number, { from: number; wayId: number }>();
  const heap = new MinHeap();

  gScore.set(from, 0);
  heap.push(from, haversineM(await g.vertex(from), targetCoord));

  const startTime = deadlineMs != null ? performance.now() : 0;
  let expansions = 0;
  let reached = false;
  for (let u = heap.pop(); u !== null; u = heap.pop()) {
    if (signal?.aborted) return null;
    if (deadlineMs != null && expansions % 64 === 0 && performance.now() - startTime >= deadlineMs) {
      return null;
    }
    if (u === to) {
      reached = true;
      break;
    }
    expansions++;
    if (expansions > maxExpansions) {
      console.debug(`astar.route: gave up past maxExpansions (${maxExpansions})`);
      return null;
    }
    const du = gScore.get(u) ?? Infinity;
    // Read u's edges, then resolve every neighbor's coordinate in
    // parallel (one round-trip covers the whole fan-out instead of one
    // per edge) — but relax strictly in edge order afterward, using the
    // now-resolved coords. The relax loop itself has no further await
    // points, so heap push order is identical to a fully sequential
    // version: determinism is a hard requirement (this mirrors the
    // phone's Router.swift and must produce identical paths), and
    // parallelizing only the READS, never the relax decisions, is what
    // buys back network overlap without touching that guarantee.
    const edges = await g.edges(u);
    const neighborCoords = await Promise.all(edges.map((edge) => g.vertex(edge.target)));
    for (let i = 0; i < edges.length; i++) {
      const edge = edges[i];
      const nd = du + edge.meters;
      if (nd < (gScore.get(edge.target) ?? Infinity)) {
        gScore.set(edge.target, nd);
        cameFrom.set(edge.target, { from: u, wayId: edge.wayId });
        heap.push(edge.target, nd + haversineM(neighborCoords[i], targetCoord));
      }
    }
    // Speculative warm-up for the frontier's likely next few pops —
    // fire-and-forget (dlr2.ts's prefetchEdges never throws and returns
    // void), so it can only help the next iteration's g.edges() land warm
    // in the block cache, never slow this one down or change its result.
    for (const id of heap.topIds(3)) g.prefetchEdges(id);
  }

  if (!reached) return null;

  const path = [to];
  const wayIds: number[] = [];
  while (cameFrom.has(path[path.length - 1])) {
    const step = cameFrom.get(path[path.length - 1])!;
    wayIds.push(step.wayId);
    path.push(step.from);
  }
  path.reverse();
  wayIds.reverse();
  if (path[0] !== from) return null; // defensive; shouldn't happen

  // Batch the coordinate reads for the final path rather than resolving
  // them one at a time — the search itself must stay sequential (A*
  // expands one node at a time in priority order) but rendering geometry
  // has no such constraint.
  const coords = await Promise.all(path.map((id) => g.vertex(id)));
  console.debug(`astar.route: ${from} -> ${to} in ${expansions} expansions, ${path.length} vertices`);
  return { path, meters: gScore.get(to) ?? 0, coords, expansions, wayIds };
}
